import { CheckCircle } from "@mui/icons-material";
import { Box, CircularProgress, Typography } from "@mui/material";
import { useTranslation } from "common/locales";
import type { Feature, Point } from "geojson";
import { useEffect, useMemo, useRef, useState } from "react";

const STATUS_GREEN = "#166534";
const STATUS_GREEN_BORDER = "rgba(22, 101, 52, 0.28)";

export type TileStatus = "computing" | "done";

interface MarkerProps {
  status: TileStatus;
}

export function FieldTileStatusMarker({ status }: MarkerProps) {
  const { t } = useTranslation();
  const isDone = status === "done";

  return (
    <Box
      sx={{
        alignItems: "center",
        bgcolor: "rgba(255, 255, 255, 0.92)",
        border: `1px solid ${STATUS_GREEN_BORDER}`,
        borderRadius: "999px",
        boxShadow: "0 2px 8px rgba(15, 23, 42, 0.18)",
        display: "flex",
        gap: 0.75,
        px: 1,
        py: 0.5,
      }}
    >
      {isDone ? (
        <CheckCircle sx={{ color: STATUS_GREEN, fontSize: 14 }} />
      ) : (
        <CircularProgress
          size={14}
          thickness={5}
          sx={{ color: STATUS_GREEN }}
        />
      )}
      <Typography
        variant="caption"
        sx={{
          color: STATUS_GREEN,
          fontSize: "0.7rem",
          fontWeight: 700,
          lineHeight: 1,
          whiteSpace: "nowrap",
        }}
      >
        {isDone ? t("farm.cover.done") : t("farm.cover.computing")}
      </Typography>
    </Box>
  );
}

export const FIELD_TILE_STATUS_STYLE = {
  fillColor: "#F7F3E8",
  fillOpacity: 0.28,
  lineColor: STATUS_GREEN,
};

interface RecentlyCompletedOptions {
  editedFieldId?: string;
  fieldIds?: string[];
  durationMs?: number;
}

export function useRecentlyCompletedTileFields(
  currentNoTileIds: string[],
  centersById: Record<string, Feature<Point>>,
  { editedFieldId, fieldIds, durationMs = 3500 }: RecentlyCompletedOptions = {}
): Array<{ guid: string; center: Feature<Point> }> {
  const previousNoTileIds = useRef<Set<string> | null>(null);
  const timeoutIds = useRef<number[]>([]);
  const [doneIds, setDoneIds] = useState<string[]>([]);

  const visibleFieldIds = useMemo(
    () => (fieldIds && fieldIds.length > 0 ? new Set(fieldIds) : undefined),
    [fieldIds]
  );

  useEffect(() => {
    const current = new Set(currentNoTileIds);
    const previous = previousNoTileIds.current;

    if (previous) {
      const completedIds = [...previous].filter((id) => {
        if (current.has(id)) return false;
        if (id === editedFieldId) return false;
        if (visibleFieldIds && !visibleFieldIds.has(id)) return false;
        return Boolean(centersById[id]);
      });

      if (completedIds.length > 0) {
        setDoneIds((existing) =>
          Array.from(new Set([...existing, ...completedIds]))
        );
        const timeoutId = window.setTimeout(() => {
          setDoneIds((existing) =>
            existing.filter((id) => !completedIds.includes(id))
          );
        }, durationMs);
        timeoutIds.current.push(timeoutId);
      }
    }

    previousNoTileIds.current = current;
  }, [
    centersById,
    currentNoTileIds,
    durationMs,
    editedFieldId,
    visibleFieldIds,
  ]);

  useEffect(() => {
    return () => {
      timeoutIds.current.forEach(window.clearTimeout);
      timeoutIds.current = [];
    };
  }, []);

  return doneIds
    .filter((guid) => {
      if (guid === editedFieldId) return false;
      if (visibleFieldIds && !visibleFieldIds.has(guid)) return false;
      return Boolean(centersById[guid]);
    })
    .map((guid) => ({
      guid,
      center: centersById[guid],
    }));
}
